/**
 * Phase 6-C / Journey B — Client Portal Reports.
 *
 * Reads `/api/v1/client-portal/reports` (placeholder — returns []).
 * Each report links to a Drive URL. Brand-aware header + back action so it
 * reads as part of the same white-label portal as the dashboard.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Link as MuiLink,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { RootState } from '../../store/store';
import {
  ClientPortalReport,
  getReports,
} from '../../lib/api/client-portal/client-portal-api';
import { getPortalBrand } from './brand';

const ClientPortalReports: React.FC = () => {
  const accessToken = useSelector(
    (s: RootState) => s.auth.clientPortal.accessToken
  );
  const identity = useSelector((s: RootState) => s.auth.clientPortal.identity);

  const [reports, setReports] = useState<ClientPortalReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const brand = useMemo(() => getPortalBrand(identity), [identity]);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await getReports(accessToken);
        if (!cancelled) setReports(list);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[ClientPortalReports] load failed', err);
        if (!cancelled) setError('Could not load reports.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f6f8' }}>
      <Container maxWidth='md' sx={{ py: { xs: 3, md: 4 } }}>
        <Stack spacing={3}>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            spacing={2}
          >
            <Box>
              <Typography
                variant='overline'
                sx={{ color: brand.primary, fontWeight: 700 }}
              >
                {brand.shopName}
              </Typography>
              <Typography variant='h5' component='h1' sx={{ fontWeight: 700 }}>
                Reports
              </Typography>
            </Box>
            <Button
              component={Link}
              to='/client-portal/dashboard'
              variant='outlined'
              size='small'
              sx={{
                color: brand.primary,
                borderColor: brand.primary,
                '&:hover': {
                  borderColor: brand.primary,
                  backgroundColor: brand.softBg,
                },
              }}
            >
              Back to dashboard
            </Button>
          </Stack>

          {error && <Alert severity='warning'>{error}</Alert>}

          <Paper elevation={0} variant='outlined' sx={{ p: 3, borderRadius: 3 }}>
            {loading ? (
              <Stack direction='row' alignItems='center' spacing={2}>
                <CircularProgress size={20} sx={{ color: brand.primary }} />
                <Typography color='text.secondary'>
                  Loading reports…
                </Typography>
              </Stack>
            ) : reports.length === 0 ? (
              <Stack spacing={1}>
                <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                  No reports yet
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Your agency publishes a performance summary here each week.
                  Check back soon — we'll email you when the first one is ready.
                </Typography>
              </Stack>
            ) : (
              <List disablePadding>
                {reports.map((r) => (
                  <ListItem key={r.id} disableGutters>
                    <ListItemText
                      primary={
                        r.drive_url ? (
                          <MuiLink
                            href={r.drive_url}
                            target='_blank'
                            rel='noopener noreferrer'
                            sx={{ color: brand.primary, fontWeight: 600 }}
                          >
                            {r.title || `Report ${r.id}`}
                          </MuiLink>
                        ) : (
                          r.title || `Report ${r.id}`
                        )
                      }
                      secondary={r.generated_at || undefined}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
};

export default ClientPortalReports;
